import { Component, createContext, useCallback, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react';
import type { CompiledSurvey } from '@apruv/survey-contract/compiler';
import type { CXPublicSurveyResponse } from '../../services/cxPublicSurveyService';
import { canonicalExecutionPolicy } from './canonicalSurveyContext';
import { recordSurveyOutcome, type SurveyObservationScope, type SurveyOutcome } from './surveyObservability';

type Observer = (event: SurveyOutcome) => void;
export const SurveyObservationContext = createContext<Observer>(() => {});
export const useSurveyObservation = () => useContext(SurveyObservationContext);

/** Call only after the public document/context has passed normal preparation. */
export function surveyObservationScope(response: CXPublicSurveyResponse, compiled?: CompiledSurvey | null): SurveyObservationScope {
  const snapshot = compiled ? response.runtimeContext?.flagSnapshot : undefined;
  const flag = snapshot && typeof snapshot === 'object' && !Array.isArray(snapshot) ? snapshot as Record<string, unknown> : {};
  const id = compiled?.snapshot.identity;
  return {
    enabled: response.testMode !== true && canonicalExecutionPolicy(response).preview !== true,
    presentation: compiled ? 'feedback_2026' : 'legacy',
    organizationId: id?.organizationId ?? response.runtimeContext?.organizationId,
    experienceId: id?.experienceId,
    experienceVersionId: id?.experienceVersionId,
    flagRunId: typeof flag.runId === 'string' ? flag.runId : undefined,
    flagStepId: typeof flag.stepId === 'string' ? flag.stepId : undefined,
  };
}
export const observerFor = (scope: SurveyObservationScope): Observer => (event) => recordSurveyOutcome(scope, event);

/** Each selected asset reports ready/failure at most once per outcome. Its URL never leaves this hook. */
export function useMediaObservation(kind: 'image' | 'video' | 'voice' | undefined, key: string | undefined) {
  const observe = useSurveyObservation();
  // An abandoned concurrent render owns a separate set and cannot reset the committed asset.
  const seen = useMemo(() => new Set<string>(), [key, kind]);
  const report = useCallback((outcome: 'success' | 'failure') => {
    if (!kind || !key || seen.has(outcome)) return;
    seen.add(outcome);
    observe({ category: 'media', detail: kind, outcome });
  }, [kind, key, observe, seen]);
  return { ready: () => report('success'), failed: () => report('failure') };
}

function CommittedSurvey({ children }: { children: ReactNode }) {
  const observe = useSurveyObservation();
  const reported = useRef(false);
  useEffect(() => {
    if (reported.current) return;
    reported.current = true;
    observe({ category: 'render', detail: 'document', outcome: 'success' });
  }, [observe]);
  return <>{children}</>;
}
/** Observe failure, then preserve the existing outer boundary's session unmount and retry. */
class RenderFailureObserver extends Component<{ observe: Observer; children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(error: Error) {
    try { this.props.observe({ category: 'render', detail: 'document', outcome: 'failure' }); }
    finally { throw error; }
  }
  render() { return this.state.failed ? null : this.props.children; }
}
export function ObservedSurveyRender({ children }: { children: ReactNode }) {
  const observe = useSurveyObservation();
  return <RenderFailureObserver observe={observe}><CommittedSurvey>{children}</CommittedSurvey></RenderFailureObserver>;
}
